import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, MapPin, Users, Lock, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { articlesAPI, initiativesAPI, mediaAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { HeritageImagePopup } from '../components/common/HeritageImagePopup';
import { convertGoogleDriveUrl } from '../lib/imageUtils';

const HERO_IMAGE = "https://images.unsplash.com/photo-1705291490318-6cc8cb88a1f3?crop=entropy&cs=srgb&fm=jpg&q=85";

// Placeholder image for articles without featured images
const HERITAGE_IMAGE = "https://images.unsplash.com/photo-1638738105885-3e43f3b156a3?crop=entropy&cs=srgb&fm=jpg&q=85";

// Town Hall heritage image
const TOWN_HALL_IMAGE = "https://customer-assets.emergentagent.com/job_4bd2b615-e305-456c-8451-8b16b69acec6/artifacts/57eok28j_Enhanced_Town%20Hall.png";

// Town Hall popup content
const townHallContent = {
  title: "Jubbulpore Municipal Town Hall",
  subtitle: "Erected: 1890",
  bodyText: [
    "Financed by Raja Gokuldas, this Town Hall was presented to the people of Jubbulpore as a civic institution during the Jubilee era of Queen Victoria. It was established to serve as a formal municipal and public assembly space for the growing colonial city.",
    "The building is designed in the Indo-Saracenic architectural style — a late 19th-century fusion of Mughal, Rajput, and British civic design. This architectural vocabulary was widely adopted for colonial-era public buildings in Jabalpur and remains visibly embedded in the city's urban landscape even today."
  ]
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, initiativesRes, mediaRes] = await Promise.all([
          articlesAPI.getAll(),
          initiativesAPI.getAll(),
          mediaAPI.getAll()
        ]);
        setArticles(articlesRes.data.slice(0, 3));
        setInitiatives(initiativesRes.data.slice(0, 2));
        setMediaItems(mediaRes.data.slice(0, 3));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="paper-texture" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-primary/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-primary-foreground mb-6 tracking-tight">
              Jabalipuraan
            </h1>
            <p className="font-accent text-xl md:text-2xl italic text-primary-foreground/90 mb-4">
              "Heritage by Jabalpuriyas, for Jabalpuriyas, and of Jabalpuriyas."
            </p>
            <p className="font-body text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              A citizen-led digital heritage platform documenting the historical, 
              cultural, and social narratives of Jabalpur.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/articles">
                <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium px-8" data-testid="explore-btn">
                  Explore Heritage
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium px-8" data-testid="learn-more-btn">
                  Learn More
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-accent font-medium text-sm uppercase tracking-widest">Our Vision</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2 mb-6">
                Preserving Jabalpur's Rich Heritage
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Jabalipuraan makes Jabalpur's heritage publicly visible and understandable, 
                academically credible, and usable for civic pride, heritage conservation, 
                skill development, and tourism.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: BookOpen, label: 'Research Archive', desc: 'Curated scholarly content' },
                  { icon: MapPin, label: 'Local Heritage', desc: 'Stories of Jabalpur' },
                  { icon: Users, label: 'Citizen-Led', desc: 'By the community' },
                  { icon: Lock, label: 'Gated Access', desc: 'Protect valuable research' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="p-2 bg-accent/10 rounded">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{item.label}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <HeritageImagePopup
                imageSrc={TOWN_HALL_IMAGE}
                imageAlt="Jubbulpore Municipal Town Hall, erected 1890"
                title={townHallContent.title}
                subtitle={townHallContent.subtitle}
                bodyText={townHallContent.bodyText}
                className="w-full h-[400px] shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold Divider */}
      <div className="gold-line mx-auto max-w-7xl"></div>

      {/* Featured Articles */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-accent font-medium text-sm uppercase tracking-widest">Stories</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2">
                Featured Articles
              </h2>
            </div>
            <Link to="/articles" className="hidden sm:flex items-center gap-2 text-foreground hover:text-accent transition-colors duration-300">
              <span className="font-medium">View All</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-muted h-48 mb-4"></div>
                  <div className="bg-muted h-6 w-3/4 mb-2"></div>
                  <div className="bg-muted h-4 w-full"></div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {articles.map((article, idx) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="article-card group"
                >
                  <Link to={`/articles/${article.id}`} data-testid={`article-card-${idx}`}>
                    <div className="img-zoom mb-4 border border-border">
                      <img 
                        src={article.featured_image || HERITAGE_IMAGE}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="tag-badge">{article.category}</span>
                      {article.visibility === 'restricted' && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Lock className="h-3 w-3" /> Gated
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                      {article.title}
                    </h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">
                      {article.excerpt}
                    </p>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No articles yet. Check back soon!</p>
              {!isAuthenticated && (
                <Link to="/register">
                  <Button>Register for Updates</Button>
                </Link>
              )}
            </div>
          )}

          <div className="sm:hidden mt-8 text-center">
            <Link to="/articles">
              <Button variant="outline">View All Articles</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Media Coverage Section */}
      {mediaItems.length > 0 && (
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-accent font-medium text-sm uppercase tracking-widest">Press</span>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2">
                  Media Coverage
                </h2>
              </div>
              <Link to="/media" className="hidden sm:flex items-center gap-2 text-foreground hover:text-accent transition-colors duration-300">
                <span className="font-medium">View All</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {mediaItems.map((item, idx) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="group bg-background border border-border hover:border-accent/50 transition-colors duration-300"
                >
                  <Link to={`/media/${item.id}`} className="flex gap-4 p-4" data-testid={`media-card-${idx}`}>
                    <div className="img-zoom w-20 h-20 flex-shrink-0 border border-border">
                      <img 
                        src={convertGoogleDriveUrl(item.image) || HERO_IMAGE}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                        <span className="font-medium text-accent">{item.source}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(item.date).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground group-hover:text-accent transition-colors duration-300 line-clamp-2">
                        {item.title}
                      </h3>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            <div className="sm:hidden mt-8 text-center">
              <Link to="/media">
                <Button variant="outline">View All Media Coverage</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Initiatives Section */}
      {initiatives.length > 0 && (
        <section className="py-16 md:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-accent font-medium text-sm uppercase tracking-widest">Projects</span>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2">
                  Our Initiatives
                </h2>
              </div>
              <Link to="/initiatives" className="hidden sm:flex items-center gap-2 text-foreground hover:text-accent transition-colors duration-300">
                <span className="font-medium">View All</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {initiatives.map((initiative, idx) => (
                <motion.div
                  key={initiative.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-muted/30 p-6 md:p-8 border border-border hover:border-accent/50 transition-colors duration-300"
                >
                  <Link to={`/initiatives/${initiative.id}`} data-testid={`initiative-card-${idx}`}>
                    {initiative.featured_image && (
                      <div className="img-zoom mb-6 -mx-6 -mt-6 md:-mx-8 md:-mt-8">
                        <img 
                          src={initiative.featured_image}
                          alt={initiative.title}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    )}
                    <span className={`inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider mb-4 ${
                      initiative.status === 'ongoing' ? 'bg-green-100 text-green-800' :
                      initiative.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {initiative.status}
                    </span>
                    <h3 className="font-heading text-2xl font-semibold text-foreground mb-3">
                      {initiative.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {initiative.description}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="sm:hidden mt-8 text-center">
              <Link to="/initiatives">
                <Button variant="outline">View All Initiatives</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Access the Full Archive
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
            Register to access complete articles, detailed citations, historical references, 
            and the full depth of Jabalpur's heritage documentation.
          </p>
          {!isAuthenticated ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium px-8" data-testid="cta-register-btn">
                  Register Now
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium px-8">
                  Already a Member? Login
                </Button>
              </Link>
            </div>
          ) : (
            <Link to="/articles">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium px-8">
                Browse Full Archive
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
